import { useRef } from 'react'

type Writer<T> = (value: T) => Promise<void>

/**
 * Serializes repeated writes of the same piece of state so they can't race.
 *
 * Drag-and-drop reordering fires a write per drag, and a user dragging quickly
 * can start a second one before the first has come back. Without this, the two
 * requests race and whichever lands last wins — which is not necessarily the
 * order the user left on screen.
 *
 * Only one write runs at a time. Anything requested while one is in flight
 * replaces the queued value instead of joining a growing queue, so a burst of N
 * drags costs at most two requests and always ends on the newest order.
 *
 * The writer is expected to handle its own errors (the callers alert and reload
 * from the server); a rejection here drops whatever was queued behind it, which
 * is what you want when the next step is a reload anyway.
 */
export const useCoalescedWrite = <T,>() => {
  const pending = useRef<{ value: T; write: Writer<T> } | null>(null)
  const isWriting = useRef(false)

  return async (value: T, write: Writer<T>) => {
    // Overwrite rather than append: only the newest order is worth sending.
    pending.current = { value, write }
    if (isWriting.current) return

    isWriting.current = true
    try {
      while (pending.current) {
        const next = pending.current
        pending.current = null
        await next.write(next.value)
      }
    } finally {
      isWriting.current = false
    }
  }
}
